import { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next/typescript/t";

import {
  useLookupOrganizationDomainsMutation,
  useSubmitApplicationMutation,
} from "@/redux/service";
import { handleErrors, snakeToCamelAttr } from "@/shared/utils/forms";
import { DomainLookupResultResponse } from "@/redux/organizations/types";
import { ApplicationResponse } from "@/redux/applications/types";
import { ApplyToJoinFormValues } from "../types";

export const APPLY_STEP = {
  EMAIL: 0,
  DETAILS: 1,
} as const;

const NO_ORGANIZATION_FOUND_CODE = "no_organization_found";

const applyToJoinSchema = (
  t: TFunction,
): yup.ObjectSchema<ApplyToJoinFormValues> =>
  yup.object().shape({
    email: yup
      .string()
      .email(t("applications.validation.enterValidEmail"))
      .typeError(t("applications.validation.emailNotValid"))
      .required(t("applications.validation.enterValidEmail")),
    firstName: yup
      .string()
      .typeError(t("applications.validation.firstNameNotValid"))
      .required(t("applications.validation.firstNameRequired")),
    lastName: yup
      .string()
      .typeError(t("applications.validation.lastNameNotValid"))
      .required(t("applications.validation.lastNameRequired")),
    note: yup.string().typeError(t("applications.validation.notValid")),
    organization: yup
      .number()
      .typeError(t("applications.validation.notValid"))
      .required(t("applications.validation.required")),
  });

export const useApplyToJoin = (
  onNoOrganizationFound: (email: string) => void,
) => {
  const { notification } = App.useApp();
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState<number>(APPLY_STEP.EMAIL);
  const [organizations, setOrganizations] = useState<
    DomainLookupResultResponse[]
  >([]);
  const [isOpenRegistration, setIsOpenRegistration] = useState(false);
  const [result, setResult] = useState<ApplicationResponse | null>(null);

  const [lookupOrganizationDomains, { isLoading: isCheckingEmail }] =
    useLookupOrganizationDomainsMutation();
  const [submitApplication, { isLoading: isSubmitting }] =
    useSubmitApplicationMutation();

  const form = useForm<ApplyToJoinFormValues>({
    resolver: yupResolver(applyToJoinSchema(t)),
    mode: "onChange",
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      note: "",
      organization: undefined,
    },
  });

  const handleNext = async () => {
    const isValid = await form.trigger("email");
    if (!isValid) return;

    const email = form.getValues("email");
    lookupOrganizationDomains({ email })
      .unwrap()
      .then((data) => {
        setOrganizations(data);
        setIsOpenRegistration(false);
        form.setValue(
          "organization",
          data.length === 1 ? data[0]?.id : undefined,
        );
        setCurrentStep(APPLY_STEP.DETAILS);
      })
      .catch((error) => {
        if (error?.data?.errors?.[0]?.code === NO_ORGANIZATION_FOUND_CODE) {
          onNoOrganizationFound(email);
          setIsOpenRegistration(true);
          setCurrentStep(APPLY_STEP.DETAILS);
        } else {
          handleErrors(
            error,
            form.setError,
            notification,
            undefined,
            snakeToCamelAttr,
          );
        }
      });
  };

  const handleBack = () => {
    setCurrentStep(APPLY_STEP.EMAIL);
    setIsOpenRegistration(false);
  };

  const onValidSubmit = (values: ApplyToJoinFormValues) => {
    if (values.organization == null) return;

    submitApplication({ ...values, organization: values.organization })
      .unwrap()
      .then((data) => {
        setResult(data);
      })
      .catch((errors) =>
        handleErrors(
          errors,
          form.setError,
          notification,
          undefined,
          snakeToCamelAttr,
        ),
      );
  };

  return {
    currentStep,
    form,
    organizations,
    isOpenRegistration,
    result,
    isCheckingEmail,
    isSubmitting,
    handleNext,
    handleBack,
    handleSubmit: form.handleSubmit(onValidSubmit),
  };
};
